import { getProductConfig } from '@/app/config/products.config';
import {
  ProposalAnalysisDetails,
  type ProposalAnalysisTab,
} from '@/features/proposal-analyses/components';
import { PmeProposalAnalysisPage } from '@/features/proposal-analyses/pme';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import {
  requireInsurerUnderwritingAccess,
  requirePermissions,
} from '@/shared/utils';
import { Spinner } from '@noorden-insurtech/alba-design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

const proposalAnalysisSearchSchema = z.object({
  tab: z.enum(['resume', 'observations', 'interactions']).optional(),
});

export const Route = createFileRoute('/_app/proposal-analyses/$proposalId/')({
  component: RouteComponent,
  validateSearch: proposalAnalysisSearchSchema,
  beforeLoad: async () => {
    const [hasPermission, hasUnderwritingAccess] = await Promise.all([
      requirePermissions(['policies.proposals.read']),
      requireInsurerUnderwritingAccess(),
    ]);
    if (!hasPermission || !hasUnderwritingAccess) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function RouteComponent() {
  const { proposalId } = Route.useParams();
  const { tab } = Route.useSearch();

  return (
    <ProposalAnalysisRouteResolver
      proposalId={proposalId}
      initialTab={(tab as ProposalAnalysisTab) ?? 'resume'}
    />
  );
}

type ResolverProps = {
  proposalId: string;
  initialTab: ProposalAnalysisTab;
};

function ProposalAnalysisRouteResolver({
  proposalId,
  initialTab,
}: ResolverProps) {
  const { data: proposal, isLoading } = useProposalDetails(proposalId);

  if (isLoading) {
    return (
      <div className='flex justify-center py-24'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  // Produtos Group Life PME usam a tela de análise dedicada (layout da análise
  // de cotação PME); os demais seguem a análise de proposta empresarial.
  if (getProductConfig(proposal?.productCode ?? '')?.usesGroupLifeProposal) {
    return (
      <PmeProposalAnalysisPage
        proposalId={proposalId}
        initialTab={initialTab}
      />
    );
  }

  return (
    <ProposalAnalysisDetails proposalId={proposalId} initialTab={initialTab} />
  );
}
